import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

class PrivacyPolicy extends BaseComponent {
  static styles = css`
    :host {
      display: block;
    }

    h2 {
      font-size: 1.25rem;
      margin-top: 1.5rem;
    }

    h3 {
      font-size: 1.1rem;
      margin-top: 1rem;
    }

    ul {
      padding-left: 1.5rem;
    }

    table {
      width: 100%;
      margin: 1rem 0;
    }

    th,
    td {
      padding: 0.5rem;
      text-align: left;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    th {
      font-weight: 600;
    }
  `;

  render() {
    return html`
      <p><strong>Last Updated: January 2026</strong></p>
      <p>
        This Privacy Policy describes how Dice Driven Design LLC ("Company," "we," "us," or "our") collects, uses, and
        shares information about you when you use the RPGish platform, including our websites, applications, and
        services (collectively, the "Service").
      </p>
      <p>
        By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>1.1 Information You Provide</h3>
      <p><strong>Account Information</strong>: When you create an account, we collect:</p>
      <ul>
        <li>Email address</li>
        <li>Username</li>
        <li>Name</li>
        <li>Date of birth (stored for ongoing age compliance verification, not just initial registration)</li>
        <li>Password (stored securely by our authentication provider)</li>
      </ul>
      <p>
        <strong>Profile Information</strong>: You may choose to provide additional information such as a profile
        picture.
      </p>
      <p><strong>Game Data</strong>: We collect information about your gameplay, including:</p>
      <ul>
        <li>Characters you create</li>
        <li>Game sessions and progress</li>
        <li>In-game choices and actions</li>
        <li>Adventures you play or create</li>
      </ul>
      <p>
        <strong>Communications</strong>: If you contact us for support or feedback, we collect the content of those
        communications.
      </p>

      <h3>1.2 Information Collected Automatically</h3>
      <p>
        <strong>Usage Information</strong>: We automatically collect information about how you use the Service,
        including:
      </p>
      <ul>
        <li>Features you use</li>
        <li>Actions you take</li>
        <li>Time, frequency, and duration of activities</li>
      </ul>
      <p>
        <strong>Device Information</strong>: We collect information about the device you use to access the Service,
        including:
      </p>
      <ul>
        <li>Device type and operating system</li>
        <li>Browser type</li>
        <li>IP address</li>
        <li>General location (country/region level)</li>
      </ul>
      <p><strong>Cookies and Similar Technologies</strong>: We use cookies and similar technologies to:</p>
      <ul>
        <li>Keep you logged in</li>
        <li>Remember your preferences</li>
        <li>Understand how you use the Service</li>
        <li>Improve the Service</li>
      </ul>

      <h3>1.3 Information from Third Parties</h3>
      <p>
        <strong>Authentication Provider</strong>: We use Auth0 for authentication. When you sign in, we receive basic
        profile information from Auth0.
      </p>
      <p>
        <strong>Social Login</strong>: If you choose to sign in with a social account (such as Google), we receive
        information permitted by your social account settings.
      </p>

      <h3>1.4 Playtest-Specific Data</h3>
      <p>During the Playtest period, we collect additional information to help us improve the Service:</p>
      <ul>
        <li>
          <strong>Bug Reports</strong>: Information you submit about issues or problems, including steps to reproduce
        </li>
        <li>
          <strong>Gameplay Analytics</strong>: Data about how you interact with game features, including usage patterns
          and feature engagement
        </li>
        <li>
          <strong>Performance Data</strong>: Technical metrics about how the Service performs, including load times and
          error rates
        </li>
        <li>
          <strong>Session Logs</strong>: Detailed logs of game sessions for debugging purposes, which may include
          in-game actions and events
        </li>
      </ul>
      <p>
        This Playtest-specific data helps us identify and fix issues, improve game balance, and enhance the overall
        experience.
      </p>

      <h3>1.5 Cookies and Similar Technologies</h3>
      <p>We use cookies to operate the Service. The following table describes the cookies we use:</p>
      <table>
        <thead>
          <tr>
            <th>Cookie Type</th>
            <th>Purpose</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Session Cookies</td>
            <td>Keep you logged in and maintain your session</td>
            <td>Duration of browser session</td>
          </tr>
          <tr>
            <td>Preference Cookies</td>
            <td>Remember your settings and preferences</td>
            <td>1 year</td>
          </tr>
        </tbody>
      </table>
      <p>
        We do not currently use analytics or advertising cookies. If this changes, we will update this Privacy Policy
        and notify you.
      </p>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>
          <strong>Provide the Service</strong>: Create and manage your account, enable gameplay, save your progress
        </li>
        <li><strong>Improve the Service</strong>: Analyze usage patterns, identify bugs, develop new features</li>
        <li>
          <strong>Communicate with You</strong>: Send service-related notices, respond to inquiries, provide support
        </li>
        <li><strong>Ensure Safety and Security</strong>: Detect and prevent fraud, abuse, and security incidents</li>
        <li><strong>Comply with Legal Obligations</strong>: Meet legal requirements and respond to lawful requests</li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

      <h3>3.1 With Other Players</h3>
      <ul>
        <li>Your username and profile picture are visible to other players in multiplayer games</li>
        <li>Your gameplay actions are visible to other players in shared game sessions</li>
      </ul>

      <h3>3.2 With Service Providers</h3>
      <p>
        We share information with third-party service providers who help us operate the Service. These providers are
        contractually obligated to protect your information and use it only for the purposes we specify.
      </p>
      <table>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Data Shared</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Amazon Web Services (AWS)</td>
            <td>Cloud hosting and infrastructure</td>
            <td>All Service data</td>
          </tr>
          <tr>
            <td>Auth0</td>
            <td>Authentication and identity management</td>
            <td>Account credentials and login data</td>
          </tr>
        </tbody>
      </table>

      <h3>3.3 For Legal Reasons</h3>
      <p>We may disclose your information if required by law or if we believe disclosure is necessary to:</p>
      <ul>
        <li>Comply with legal process</li>
        <li>Protect the rights, property, or safety of the Company, our users, or the public</li>
        <li>Detect or prevent fraud or security issues</li>
      </ul>

      <h3>3.4 Business Transfers</h3>
      <p>
        If the Company is involved in a merger, acquisition, or sale of assets, your information may be transferred as
        part of that transaction.
      </p>

      <h2>4. Data Retention</h2>
      <p>
        We retain different types of information for different periods based on the purpose for which it was collected:
      </p>
      <table>
        <thead>
          <tr>
            <th>Data Type</th>
            <th>Retention Period</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Account Information</td>
            <td>Duration of account + 30 days after deletion request</td>
          </tr>
          <tr>
            <td>Gameplay Data (characters, progress, saves)</td>
            <td>Duration of account + 90 days after deletion</td>
          </tr>
          <tr>
            <td>Communications (support requests, emails)</td>
            <td>2 years</td>
          </tr>
          <tr>
            <td>Analytics Data</td>
            <td>Aggregated and anonymized indefinitely</td>
          </tr>
          <tr>
            <td>Playtest Feedback</td>
            <td>Indefinitely (becomes Company property per the Playtest NDA)</td>
          </tr>
        </tbody>
      </table>
      <p>
        We may retain certain information beyond these periods if required for legal compliance, fraud prevention,
        enforcing our agreements, or resolving disputes.
      </p>

      <h2>5. Your Rights and Choices</h2>
      <h3>5.1 Account Information</h3>
      <p>You can access and update your account information through the Service settings.</p>

      <h3>5.2 Account Deletion</h3>
      <p>
        You may request deletion of your account by contacting us at privacy@rpgish.com. We will delete your account and
        associated personal data, subject to legal retention requirements.
      </p>

      <h3>5.3 Cookies</h3>
      <p>
        Most browsers allow you to refuse cookies or alert you when cookies are being sent. Note that some features of
        the Service may not function properly without cookies.
      </p>

      <h3>5.4 Communications</h3>
      <p>
        You can opt out of promotional communications by following the unsubscribe instructions in those messages. You
        cannot opt out of service-related communications.
      </p>

      <h3>5.5 Do Not Track</h3>
      <p>
        Some browsers offer a "Do Not Track" (DNT) setting that sends a signal to websites you visit indicating you do
        not wish to be tracked. We do not currently respond to DNT signals because there is no industry-standard
        interpretation of how to respond to such signals. If a standard is established, we will revisit this policy.
      </p>

      <h2>6. International Data Transfers</h2>
      <p>
        The Service is operated from the United States. If you are located outside the United States, your information
        will be transferred to and processed in the United States, where data protection laws may differ from those in
        your country.
      </p>
      <p>By using the Service, you consent to the transfer of your information to the United States.</p>
      <p>
        <strong>For users in the European Economic Area (EEA)</strong>: We rely on the following legal mechanisms for
        transferring your data to the United States:
      </p>
      <ul>
        <li>
          <strong>Standard Contractual Clauses</strong>: We use European Commission-approved Standard Contractual
          Clauses with our service providers to ensure adequate protection for your data
        </li>
        <li>
          <strong>Your Explicit Consent</strong>: By creating an account and accepting these terms, you explicitly
          consent to the transfer of your data to the United States
        </li>
      </ul>

      <h2>7. Children's Privacy</h2>
      <p>
        RPGish is not directed to children under 13 years of age, and we do not knowingly collect personal information
        from children under 13. If you are under 13, please do not attempt to register for RPGish or send any personal
        information to us.
      </p>
      <p>
        <strong>Age Verification</strong>: We require all users to provide their date of birth during account
        registration. Our system automatically prevents users under 13 from completing the registration process. We
        present the date of birth request in a neutral manner without indicating any age requirements, in compliance
        with COPPA guidelines.
      </p>
      <p>
        If we learn that we have collected personal information from a child under 13, we will delete that information
        as quickly as possible. If you believe that a child under 13 may have provided us with personal information,
        please contact us at privacy@rpgish.com.
      </p>
      <p>
        Parents who believe their child has submitted personal information to us and would like it deleted may contact
        us at privacy@rpgish.com. We will use reasonable efforts to delete such information from our files.
      </p>

      <h2>8. Security</h2>
      <p>
        We implement appropriate technical and organizational measures to protect your information against unauthorized
        access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or
        electronic storage is completely secure.
      </p>

      <h2>9. Third-Party Links</h2>
      <p>
        The Service may contain links to third-party websites or services. We are not responsible for the privacy
        practices of these third parties. We encourage you to review their privacy policies.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or
        by posting a notice on the Service prior to the effective date. Your continued use of the Service after the
        effective date constitutes your acceptance of the updated Privacy Policy.
      </p>

      <h2>11. Contact Us</h2>
      <p>If you have questions about this Privacy Policy or our privacy practices, please contact us at:</p>
      <p><strong>Dice Driven Design LLC</strong><br />Email: privacy@rpgish.com</p>

      <hr />

      <h2>Additional Information for Specific Jurisdictions</h2>

      <h3>For California Residents (CCPA)</h3>
      <p>California residents have additional rights under the California Consumer Privacy Act:</p>
      <ul>
        <li>
          <strong>Right to Know</strong>: You can request information about the categories and specific pieces of
          personal information we have collected about you.
        </li>
        <li><strong>Right to Delete</strong>: You can request deletion of your personal information.</li>
        <li>
          <strong>Right to Non-Discrimination</strong>: We will not discriminate against you for exercising your privacy
          rights.
        </li>
      </ul>
      <p>To exercise these rights, contact us at privacy@rpgish.com.</p>

      <h3>For European Users (GDPR)</h3>
      <p>If you are in the European Economic Area, you have additional rights:</p>
      <ul>
        <li><strong>Access</strong>: Request a copy of your personal data</li>
        <li><strong>Rectification</strong>: Request correction of inaccurate data</li>
        <li><strong>Erasure</strong>: Request deletion of your data</li>
        <li><strong>Restriction</strong>: Request restriction of processing</li>
        <li><strong>Portability</strong>: Request transfer of your data</li>
        <li><strong>Objection</strong>: Object to certain processing activities</li>
      </ul>
      <p>Our legal basis for processing your data includes:</p>
      <ul>
        <li><strong>Contract</strong>: Processing necessary to provide the Service</li>
        <li><strong>Legitimate Interests</strong>: Processing for service improvement and security</li>
        <li><strong>Consent</strong>: Processing based on your consent (which you can withdraw)</li>
      </ul>
      <p>To exercise these rights, contact us at privacy@rpgish.com.</p>
    `;
  }
}

customElements.define('privacy-policy', PrivacyPolicy);
